import { createFileRoute } from '@tanstack/react-router';
import { INQUIRY_HREF } from '../lib/product-content.js';
import React, { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { PublicLayout } from '../components/public-layout.js';
import { Hero } from '../components/hero.js';
import { SectionBlock } from '../components/section-block.js';
import { JourneyOverview } from '../components/journey-overview.js';

/**
 * Landing — 메인 페이지 (/)
 *
 * 구성:
 *   Hero  — "한 번의 도입으로 치매 진단 전 과정"
 *   Bento  — Core Strengths 3가지 (양트랙·연속성·30년 임상)
 *   Narrative — 진단 이후의 연속성
 *   6-Solution Overview — JourneyOverview (3단계 카드)
 *   Before / After comparison
 *   통합크레딧 overview
 *   Timeline — 2026~2028 정책 흐름 (원칙3: 단정 금지 — 검토 단계 표현)
 *
 * 거버넌스:
 *  - 원칙1: 자동 판정·예측·분류 표현 없음
 *  - 원칙3: 정부 정책 단정 없음 — "검토 단계", "시범사업 단계 확대" 등 사실 표현
 *  - 원칙4: "의료기관용" / "의원·병원·종합병원" 양트랙 포괄
 *  - 신뢰 톤: "지금 도입하세요" 압박 표현 없음
 */
function Page() {
  // Scroll-reveal intersection observer
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('active');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <PublicLayout>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Hero
        eyebrow="치매 진단의 전 과정 · ATN 풀스택"
        title={
          <>
            치매 진단의 모든 것.<br />
            <span>단 한 번의 도입으로.</span>
          </>
        }
        description="검사부터 영상 분석, 인지 중재까지 — 30년 신경과 임상 검증과 통합 솔루션을 바탕으로 환자의 전 과정을 신뢰로 동행하는 의료기관용 AI 도구입니다."
        ctas={[
          { label: '도입 문의하기', href: INQUIRY_HREF, variant: 'primary' },
          { label: '통합 크레딧 알아보기', href: '/credit', variant: 'outline' },
        ]}
      />

      {/* ── Core Strengths Bento ─────────────────────────────────────────── */}
      <section id="strengths" className="section-proof-apple">
        <div className="container">
          <SectionBlock
            label="Core Strengths"
            title={<>도입하는 그 순간,<br /><span>치매 진료의 준비가 됩니다.</span></>}
          />
          <div className="apple-bento">
            <div className="b-item reveal">
              <span className="b-eyebrow">복잡함 해결</span>
              <h3 className="b-title">여러 제품을<br />따로 쓸 필요 없습니다.</h3>
              <p className="b-desc">
                영상·인지·중재마다 각기 다른 회사 제품을 알아보고 계약하고 따로 정산하던 번거로움은
                끝났습니다. BeauBrain 하나로 혈액 바이오마커, SCST 인지 평가, 영상 분석,
                인지 중재까지 진료 전 과정을 해결합니다.
              </p>
            </div>
            <div className="b-item reveal">
              <span className="b-eyebrow">진료의 연속성</span>
              <h3 className="b-title">환자를 끊김 없이<br />끝까지 지원합니다.</h3>
              <p className="b-desc">
                의료기관용 PASCAL-Med와 환자 스마트폰용 비의료기기 PASCAL-Home 앱이
                환자 케어의 일관성을 지원합니다. 의료기관과 가정의 경계 없이 인지 활동이 이어집니다.
              </p>
            </div>
            <div className="b-item reveal">
              <span className="b-eyebrow">30년 임상 신뢰</span>
              <h3 className="b-title">삼성서울병원 신경과의<br />임상 자산이 받쳐줍니다.</h3>
              <p className="b-desc">
                삼성서울병원 나덕렬·서상원 교수를 중심으로 한 30년의 치매 임상 연구와 노하우가
                솔루션 설계의 토대입니다. 수만 명의 환자 데이터에서 정제된 신뢰할 수 있는
                임상 지식이 응축되어 있습니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Narrative ────────────────────────────────────────────────────── */}
      <section className="section-intro-apple">
        <div className="container">
          <h2 className="narrative-text reveal">
            진단으로 끝나지 않습니다.<br />
            환자가 집에서도 <span>인지 활동을 유지</span>하고,<br />
            자연스럽게 의료기관과 신뢰 관계를 형성합니다.
          </h2>
          <p className="narrative-sub reveal">
            BeauBrain은 단일 제품의 단편적 배치가 아닌, 치매 통합 관리를 위한 든든한 동반자입니다.
          </p>
        </div>
      </section>

      {/* ── 6-Solution Overview ──────────────────────────────────────────── */}
      <section id="solutions" className="section-solutions-horizontal">
        <div className="container reveal">
          <SectionBlock
            label="Patient Journey · 3 Stages"
            title="진료 전 과정의 통합 솔루션."
            subtitle={
              <>
                검사부터 인지 중재까지 — 진료 과정 3단계에 필요한 모든 도구가 한 곳에 있습니다.
                의원·중소병원·종합병원 어디서나 사용량 기반 클라우드(SaaS) 또는
                온프레미스로 도입할 수 있습니다.
              </>
            }
          />
          <JourneyOverview
            stages={[
              {
                stage: 'dx',
                badgeLabel: 'DX · NEW',
                step: '01 / ASSESSMENT',
                title: '검사',
                desc: (
                  <>
                    <strong>혈액 검사 + SCST 인지 평가</strong>를 같은 자리에서 진행.
                    환자 의료기관 <strong>체류 시간 약 45분</strong>으로 단축.
                    혈액 결과 회신 후 담당의가 영상 분석·치료 단계를 종합 판단합니다.
                  </>
                ),
                tools: [
                  {
                    name: 'C2N 혈액 검사',
                    cost: '추가 예정',
                    costVariant: 'new',
                    meta: '알츠하이머 바이오마커',
                    desc: 'Aβ42/40, p-Tau 217 등 정량 분석. 의료기관 채혈 후 결과 회신.',
                    highlight: true,
                  },
                  {
                    name: 'SCST',
                    costVariant: 'dx',
                    meta: '신의료기술 통과 · 2026.5',
                    desc: '5대 인지 영역 정밀 평가. 30~40분 시행, 채혈과 동시 진행.',
                    highlight: true,
                    cert: true,
                  },
                ],
                href: '/solutions/scst',
              },
              {
                stage: 'img',
                badgeLabel: 'IMG',
                step: '02 / IMAGING · 양성 시',
                title: '영상 분석',
                desc: '검사 결과 양성 시 또는 추적 관찰 시점에, PET·MRI·CT 영상의 아밀로이드(Centiloid)·뇌 위축(정상군 백분위) 정량 분석. 영상 장비 없는 의료기관도 외부 영상 업로드로 사용 가능.',
                tools: [
                  {
                    name: 'Amylo',
                    costVariant: 'img',
                    meta: 'PET · Centiloid',
                    desc: '아밀로이드 PET 정량. 표준 단위로 검사 시기별 비교 용이.',
                  },
                  {
                    name: 'Morph',
                    costVariant: 'img',
                    meta: 'MRI · CT · 정상군 백분위',
                    desc: '뇌 위축 정량. 연령 · 성별 정상군 백분위와 뇌 연령.',
                  },
                ],
                href: '/solutions/amylo',
              },
              {
                stage: 'int',
                badgeLabel: 'INT',
                step: '03 / INTERVENTION · 치료·추적',
                title: '인지 중재',
                desc: '검사·진단 이후 의료기관용 디지털 인지 중재 + 가정용 인지 활동 앱(비의료기기)으로 환자의 일상까지 진료의 연속성이 이어집니다.',
                tools: [
                  {
                    name: 'PASCAL-Med',
                    costVariant: 'int',
                    meta: '의료기관용 · 의료기기',
                    desc: '디지털 인지 중재 세션 기록. 진척도 차트 제공.',
                  },
                  {
                    name: 'PASCAL-Home',
                    cost: 'B2C',
                    costVariant: 'b2c',
                    meta: '가정용 · 비의료기기',
                    desc: '환자·보호자 직접 구독. 일상 인지 활동 앱.',
                  },
                ],
                href: '/solutions/pascal-med',
              },
            ]}
          />
        </div>
      </section>

      {/* ── Before / After ───────────────────────────────────────────────── */}
      <section className="section-ba-comparison">
        <div className="container reveal">
          <SectionBlock
            label="Why Unified Credit"
            title={<>치매 진단,<br /><span>한 회사가 함께합니다.</span></>}
            subtitle="검사부터 인지 중재까지, 진료 전 과정의 도구를 한 곳에서 제공합니다. 새 솔루션이 필요할 때마다 새 계약을 맺지 않아도 됩니다."
          />
          <div className="ba-grid">
            <div className="ba-box before">
              <span className="ba-tag before">Before — 분산 도입</span>
              <h3>여러 회사 × 여러 계약</h3>
              <ul className="ba-list">
                <li><span className="ba-icon before">×</span><span>영상·인지·중재마다 다른 회사와 별도 계약</span></li>
                <li><span className="ba-icon before">×</span><span>솔루션별 별도 정산·세금계산서</span></li>
                <li><span className="ba-icon before">×</span><span>제품마다 따로 로그인</span></li>
                <li><span className="ba-icon before">×</span><span>솔루션 추가 시 매번 새 계약·새 학습</span></li>
              </ul>
            </div>
            <div className="ba-box after">
              <span className="ba-tag after">After — BeauBrain 통합</span>
              <h3>한 회사가 함께하는 진료</h3>
              <ul className="ba-list">
                <li><span className="ba-icon after">✓</span><span>진료 전 과정의 도구가 하나의 시스템에</span></li>
                <li><span className="ba-icon after">✓</span><span>통합 세금계산서·통합 정산</span></li>
                <li><span className="ba-icon after">✓</span><span>하나의 계정으로 모든 솔루션에 로그인</span></li>
                <li><span className="ba-icon after">✓</span><span>필요한 새 솔루션도 빠르게 도입</span></li>
              </ul>
            </div>
          </div>
          <div className="ba-cta-container">
            <Link to="/credit" className="ba-cta-btn">통합 크레딧 자세히 보기 →</Link>
          </div>
        </div>
      </section>

      {/* ── 통합 크레딧 overview ──────────────────────────────────────────── */}
      <section className="section-credit-apple">
        <div className="container credit-inner">
          <div className="credit-text reveal">
            <SectionBlock
              label="Unified Credit"
              title={<>쓴 만큼, 월 단위 정산.</>}
              centered={false}
            />
            <p className="credit-desc" style={{ marginTop: 16 }}>
              모든 솔루션을 단일 라이선스 강제 계약 없이,{' '}
              <strong>통합 크레딧 하나</strong>로 사용량 기반으로 운영하세요.
              사용한 크레딧만큼 매월 청구됩니다.
            </p>
            <table className="refund-table">
              <thead>
                <tr><th>항목</th><th>방식</th><th>내용</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 700 }}>정산</td>
                  <td style={{ color: 'var(--accent)', fontWeight: 700 }}>월 1회</td>
                  <td>사용한 크레딧만큼 다음 달 청구</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>청구</td>
                  <td style={{ fontWeight: 700 }}>기관 단위</td>
                  <td>매월 청구서 한 장</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>결제</td>
                  <td style={{ color: 'var(--ink-3)' }}>카드·계좌이체·가상계좌</td>
                  <td>기관 관리 › 청구서에서 결제</td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 32 }}>
              <Link
                to="/credit"
                style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}
              >
                Unified Credit 자세히 보기 →
              </Link>
            </div>
          </div>
          <div className="credit-card-minimal reveal">
            <div className="cc-header">
              <span className="cc-logo">BeauBrain</span>
              <div className="cc-chip" />
            </div>
            <div>
              <p className="cc-label">This Month · 이번 달 사용</p>
              <p className="cc-amount">1,245 <span style={{ fontSize: 18, fontWeight: 400 }}>CR</span></p>
              <p className="cc-sub">다음 달 초 청구서로 정산</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Timeline 2026–2028 ────────────────────────────────────────────── */}
      {/*
        원칙3 준수: 정부 정책 단정 없음.
        - "시범사업 단계 확대" (확정 아님), "검토 중인 방향" 표현 사용
        - "선점" "골든 윈도우" 등 압박 표현 없음
      */}
      <section className="section-golden">
        <div className="container reveal">
          <SectionBlock
            label="Looking Ahead"
            title="의료 환경이 빠르게 바뀌고 있습니다."
            subtitle="치매 관리 정책의 축이 지역 의원 중심으로 개편되고 있습니다. 임상 도구를 선제 확보한 의원·중소병원·종합병원이 향후 치매주치의 제도의 안정적 중심 거점으로 자리잡을 수 있습니다."
          />
          <div className="timeline-minimal">
            <div className="t-node">
              <div className="t-dot" />
              <div className="t-year">2026</div>
              <div className="t-label">인프라 구축</div>
              <p className="t-desc">
                SCST 신의료기술 통과(5월)로 의료기관에서 즉시 정식 진단보조 도입 가능.
                정부 AI 조기 진단 R&amp;D 지원 기틀 형성.
              </p>
            </div>
            <div className="t-node">
              <div className="t-dot active" />
              <div className="t-year">2027</div>
              <div className="t-label">진료 거점 확장</div>
              <p className="t-desc">
                치매관리주치의 시범사업 단계 확대 검토 중. 의원·중소병원·종합병원이
                인지 진단 도구를 갖추고 치매 진료 거점 역할을 수행하는 과도기입니다.
              </p>
            </div>
            <div className="t-node">
              <div className="t-dot" />
              <div className="t-year">2028</div>
              <div className="t-label">치매주치의 확대</div>
              <p className="t-desc">
                치매관리주치의 제도 확대 시행 방향 검토. 환자 데이터를 정밀 관리하고
                임상 도구를 사전 확보한 의료기관이 안정적 진료 기반을 갖추게 됩니다.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/')({
  component: Page,
});
